import { Box, Button, Stack } from "@mui/joy";
import { theme } from "styles/theme";
import React, { useEffect, useRef, useState } from "react";
import { useOnboardingPage } from "hooks/useOnboardingPage";
import CustomTypography from "components/common/CustomTypography";
import { useMutation } from "@tanstack/react-query";
import { userApi } from "api/userApi";
import { userAtom } from "../../../../recoil/userAtom";
import { useRecoilValue } from "recoil";
import { onboardingApi, OnboardingDemographicType } from "api/onboardingApi";
import useIsInappWebview from "../../../../hooks/useIsInappWebview";
import { extractHexColor } from "../../../../styles/hexOpacity";
import { sendMessage } from "../../../../utils/webview";

interface SelectOption {
  id: number;
  label: string;
}

export const selectOptionList: SelectOption[] = [
  { id: 1, label: "직업인 (회사원, 사업자, 프리랜서 등)" },
  { id: 2, label: "학생 (중·고·대학생, 대학원생 등)" },
  { id: 3, label: "무직 (주부, 은퇴자 등)" },
];

export default function Demographic_4() {
  const [isAnimating, setIsAnimating] = useState(false); // 애니메이션 상태 추가
  const user = useRecoilValue(userAtom);

  const pageKey = "demographic_4";

  const isInappWebview = useIsInappWebview();

  useEffect(() => {
    if (!isInappWebview) return;

    const topBackground = extractHexColor(theme.vars.palette.Background.Level1);
    const bottomBackground = extractHexColor(
      theme.vars.palette.Background.Level1
    );

    if (topBackground && bottomBackground) {
      sendMessage(
        JSON.stringify({
          event: "set_safearea_color",
          params: {
            topColor: `${topBackground}`,
            bottomColor: `${bottomBackground}`,
          },
        })
      );
    }
  }, [isInappWebview]);

  const { singleSelectionId, updateSingleSelection, handleNext } =
    useOnboardingPage({
      pageKey,
      nextPageRoute: `${user?.isWebFunnelRegister ? "/onboarding_new/result/1" : "/onboarding_new/goal/1"} `,
    });

  // Session storage에서 데이터 복원
  useEffect(() => {
    const storedData = sessionStorage.getItem("demographic_data");
    if (storedData) {
      try {
        const demographicData: OnboardingDemographicType =
          JSON.parse(storedData);
        if (demographicData.jobId) {
          updateSingleSelection(demographicData.jobId);
        }
      } catch (error) {
        console.error("Failed to parse demographic data:", error);
      }
    }
  }, []);

  const { mutate: submit } = useMutation(
    (job: string) => userApi.submitJob(job),
    {
      onSuccess: () => {},
    }
  );

  function handleSubmit(optionId: number) {
    const selectedOption = selectOptionList.find((e) => e.id === optionId);

    if (selectedOption && selectedOption.label) {
      submit(selectedOption.label);
    }
  }

  // 버튼 선택 로직
  const handleOptionChange = (optionId: number) => {
    updateSingleSelection(optionId);

    // Session storage 업데이트
    const storedData = sessionStorage.getItem("demographic_data");
    const demographicData: OnboardingDemographicType = storedData
      ? JSON.parse(storedData)
      : {};
    demographicData.jobId = optionId;
    sessionStorage.setItem("demographic_data", JSON.stringify(demographicData));

    handleSubmit(optionId);

    setTimeout(() => {
      setIsAnimating(false);
      handleNext({ singleSelectionId: optionId });
    }, 300);
  };

  const { mutate: savePage } = useMutation(
    (pageId: string) => onboardingApi.savePage(pageId),
    {
      onSuccess: () => {},
    }
  );

  function useEffectOnce(callback: () => void) {
    const ref = useRef(false);

    useEffect(() => {
      if (ref.current) return;
      ref.current = true;

      if (typeof callback === "function") {
        callback();
      }
    }, []);
  }

  useEffectOnce(() => {
    savePage(pageKey);
  });

  return (
    <Stack spacing={5} sx={{ height: "100%" }}>
      <Stack spacing={2}>
        <CustomTypography
          variant="Subtitle"
          fontWeight={500}
          sx={{ color: theme.vars.palette.Content.Strong }}
        >
          {user?.onboardingType === "h"
            ? "주변 환경이 어떠한지도 치료에 있어 중요한 역할을 하지요."
            : "주변 환경이 어떠한지도 마음 관리에 있어 중요한 역할을 하지요."}
        </CustomTypography>
        <CustomTypography
          variant="Subtitle"
          fontWeight={500}
          sx={{ color: theme.vars.palette.Content.Strong }}
        >
          {"현재 어떤 일을 하시나요?"}
        </CustomTypography>
      </Stack>

      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <Stack
          direction={"column"}
          sx={{
            width: "100%",
            whiteSpace: "nowrap",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {selectOptionList.map(({ id, label }, optionIndex) => (
            <Stack key={`select_option_${id}`} sx={{ flexGrow: 1, gap: 1 }}>
              <Button
                fullWidth
                variant={"SingleSlim"}
                color={singleSelectionId === id ? "primary" : "secondary"}
                size={"lg"}
                sx={{
                  transition: "background-color 0.3s ease", // ease 애니메이션 추가
                }}
                onClick={() => {
                  handleOptionChange(id);
                }}
              >
                {label}
              </Button>
            </Stack>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
}
